import { useEffect, useMemo, useRef, useState } from 'react';

import type {
  ProjectLeadDto,
  ProjectMemberDto,
  ProjectTeamSummaryDto,
} from '@time-tracking/shared';

import {
  useAddProjectLead,
  useAddTeamToProject,
  useRemoveProjectLead,
  useRemoveTeamFromProject,
  useTasks,
  useTeams,
} from '../../../api/projects.js';
import { useSearchUsers } from '../../../api/users.js';
import { ConfirmDialog } from '../dialogs/ConfirmDialog.js';

interface MembersPaneProps {
  projectId: string;
  leads: ProjectLeadDto[];
  /** Palette teams on this project (Model B) — assignable to tasks. */
  paletteTeams: ProjectTeamSummaryDto[];
  members: ProjectMemberDto[];
  /** `project.archivedAt !== null` — freezes the project-setup controls. */
  isArchived: boolean;
  onToast: (msg: string, kind: 'success' | 'error') => void;
}

// On-brand avatar tints only (green / bluebonnet / gold / charcoal-on-grey /
// charcoal-on-warm). No red or purple — off-brand per Brand Book V2.1.
const AVATAR_COLORS = [
  { bg: '#e8f3e0', fg: '#2a6017' },
  { bg: '#e3eef5', fg: '#005f86' },
  { bg: '#fff6e0', fg: '#7a5a00' },
  { bg: '#e6ebee', fg: '#333f48' },
  { bg: '#ffe8e3', fg: '#333f48' },
];

function avatarColor(userId: string) {
  const sum = [...userId].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length] ?? { bg: '#e6ebee', fg: '#333f48' };
}

function MemberAvatar({ userId, initials }: { userId: string; initials: string }) {
  const { bg, fg } = avatarColor(userId);
  return (
    <span className="member-avatar" style={{ background: bg, color: fg }} aria-hidden="true">
      {initials}
    </span>
  );
}

function SourceKindChip({
  kind,
  teamName,
}: {
  kind: ProjectMemberDto['sourceKind'];
  teamName?: string | undefined;
}) {
  if (kind === 'lead') {
    return (
      <span
        className="source-chip lead"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
      >
        <svg width="11" height="11" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
          <path d="M6 1l1.5 3 3.5.5-2.5 2.5.6 3.5L6 9l-3.1 1.5.6-3.5L1 4.5 4.5 4z" />
        </svg>
        Project lead
      </span>
    );
  }
  if (kind === 'team') {
    return (
      <span
        className="source-chip team"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
      >
        <svg width="11" height="11" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
          <circle cx="4" cy="4" r="2" />
          <circle cx="8" cy="4" r="2" />
          <path d="M0 11c0-2.2 1.8-4 4-4h4c2.2 0 4 1.8 4 4" />
        </svg>
        via {teamName ? `${teamName} team` : 'team'}
      </span>
    );
  }
  return <span className="source-chip direct">Direct</span>;
}

// Wrapping task-name pills for the "On which tasks" cell. Shows an "All tasks"
// pill when the member is on every task, otherwise a few task names + "+N more".
function TaskPills({
  onTaskIds,
  taskNameById,
  totalTaskCount,
}: {
  onTaskIds: string[];
  taskNameById: Map<string, string>;
  totalTaskCount: number;
}) {
  const [expanded, setExpanded] = useState(false);
  if (onTaskIds.length === 0) {
    return <span style={{ color: 'var(--fg3)', fontSize: 'var(--fs-12)' }}>—</span>;
  }
  if (totalTaskCount > 0 && onTaskIds.length >= totalTaskCount) {
    return <span className="task-pill task-pill--all">All tasks</span>;
  }
  const shownIds = expanded ? onTaskIds : onTaskIds.slice(0, 2);
  const remaining = onTaskIds.length - shownIds.length;
  return (
    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {shownIds.map((id) => {
        const name = taskNameById.get(id) ?? 'Task';
        return (
          <span key={id} className="task-pill" title={name}>
            {name}
          </span>
        );
      })}
      {remaining > 0 && (
        <button
          type="button"
          className="task-pill task-pill--more"
          onClick={() => setExpanded(true)}
          aria-label={`Show ${remaining} more task${remaining !== 1 ? 's' : ''}`}
        >
          +{remaining} more
        </button>
      )}
    </span>
  );
}

const SEARCH_INPUT_STYLE: React.CSSProperties = {
  padding: '6px 10px 6px 32px',
  minHeight: 34,
  borderRadius: 4,
  border: '1px solid var(--border)',
  fontFamily: 'inherit',
  fontSize: 'var(--fs-14)',
  width: 240,
};

const ADD_PILL_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 10px',
  border: '1px dashed var(--border)',
  borderRadius: 999,
  background: 'none',
  color: 'var(--fg2)',
  fontSize: 'var(--fs-13)',
  fontFamily: 'inherit',
  cursor: 'pointer',
};

const POPOVER_LIST_STYLE: React.CSSProperties = {
  position: 'absolute',
  zIndex: 100,
  top: '100%',
  left: 0,
  right: 0,
  marginTop: 2,
  background: 'var(--ut-white, #fff)',
  border: '1px solid var(--border)',
  borderRadius: 4,
  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
  listStyle: 'none',
  padding: 0,
  maxHeight: 220,
  overflowY: 'auto',
};

// ── User search combobox (Add lead) ────────────────────────────────────────────

interface UserSearchProps {
  id: string;
  label: string;
  onSelect: (userId: string, displayName: string) => void;
  onCancel: () => void;
}

function UserSearch({ id, label, onSelect, onCancel }: UserSearchProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchResult = useSearchUsers(query);
  const results = searchResult.data ?? [];

  useEffect(() => {
    inputRef.current?.focus();
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) onCancel();
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [onCancel]);

  return (
    <div ref={containerRef} style={{ position: 'relative', minWidth: 240 }}>
      <div style={{ position: 'relative' }}>
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--fg3)',
            pointerEvents: 'none',
          }}
        >
          <circle cx="6" cy="6" r="4" />
          <path d="M9.5 9.5l3 3" />
        </svg>
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onCancel();
          }}
          placeholder="Search by name or EID…"
          autoComplete="off"
          style={SEARCH_INPUT_STYLE}
          aria-label={label}
        />
      </div>
      {/* Plain button list — each result is a real <button>, reachable by Tab and
          activated with Enter/Space natively (an honest disclosure list, not a
          combobox we don't back with arrow-key roving). */}
      {open && (
        <ul aria-label={label} style={POPOVER_LIST_STYLE}>
          {searchResult.isLoading && (
            <li style={{ padding: '8px 12px', color: 'var(--fg3)', fontSize: 'var(--fs-13)' }}>
              Searching…
            </li>
          )}
          {!searchResult.isLoading && query.trim().length >= 1 && results.length === 0 && (
            <li style={{ padding: '8px 12px', color: 'var(--fg3)', fontSize: 'var(--fs-13)' }}>
              No results for &ldquo;{query}&rdquo;
            </li>
          )}
          {results.map((u) => (
            <li key={u.userId} style={{ padding: 0 }}>
              <button
                type="button"
                onClick={() => onSelect(u.userId, u.displayName)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  font: 'inherit',
                  padding: '7px 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <span style={{ fontSize: 'var(--fs-14)', fontWeight: 'var(--fw-medium)' }}>
                  {u.displayName}
                </span>
                <span style={{ fontSize: 'var(--fs-12)', color: 'var(--fg3)' }}>{u.email}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Team picker (Add team) — client-side filter over the full roster ────────────

function TeamPicker({
  teams,
  onSelect,
  onCancel,
}: {
  teams: ProjectTeamSummaryDto[];
  onSelect: (teamId: string, name: string) => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) onCancel();
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [onCancel]);

  const q = query.trim().toLowerCase();
  const matches = q ? teams.filter((t) => t.name.toLowerCase().includes(q)) : teams;

  return (
    <div ref={containerRef} style={{ position: 'relative', minWidth: 240 }}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
        }}
        placeholder="Filter teams…"
        autoComplete="off"
        style={{ ...SEARCH_INPUT_STYLE, paddingLeft: 10 }}
        aria-label="Filter teams to add to the project"
      />
      {/* Plain button list (see UserSearch) — Tab reaches each team, Enter/Space adds it. */}
      <ul aria-label="Teams" style={POPOVER_LIST_STYLE}>
        {matches.length === 0 && (
          <li style={{ padding: '8px 12px', color: 'var(--fg3)', fontSize: 'var(--fs-13)' }}>
            {teams.length === 0 ? 'No teams available to add' : 'No teams match'}
          </li>
        )}
        {matches.map((t) => (
          <li key={t.teamId} style={{ padding: 0 }}>
            <button
              type="button"
              onClick={() => onSelect(t.teamId, t.name)}
              style={{
                width: '100%',
                textAlign: 'left',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                font: 'inherit',
                padding: '7px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: t.color,
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: 'var(--fs-14)', fontWeight: 'var(--fw-medium)' }}>
                {t.name}
              </span>
              <span style={{ fontSize: 'var(--fs-12)', color: 'var(--fg3)', marginLeft: 'auto' }}>
                {t.memberCount}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Adding is what an archived project closes, and only adding: a lead or a
// palette team is only ever a step towards staffing a task, and every staffing
// write under it is already refused by the server (409 from `upsertAssignment`).
// Offering the first half of a journey whose second half is closed is what
// leaves the manager stuck at an error they could not predict. Removal is left
// alone — nothing downstream of it is blocked — so the note below says so
// rather than claiming the section is frozen.
const ARCHIVED_HINT = 'This project is archived — restore it to add leads or teams';

export function MembersPane({
  projectId,
  leads,
  paletteTeams,
  members,
  isArchived,
  onToast,
}: MembersPaneProps) {
  const [addingLeadOpen, setAddingLeadOpen] = useState(false);
  const [leadToRemove, setLeadToRemove] = useState<ProjectLeadDto | null>(null);
  const [removingLead, setRemovingLead] = useState(false);
  const [addingTeamOpen, setAddingTeamOpen] = useState(false);
  const [teamToRemove, setTeamToRemove] = useState<ProjectTeamSummaryDto | null>(null);
  const [removingTeam, setRemovingTeam] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');

  const addLeadBtnRef = useRef<HTMLButtonElement>(null);
  const addTeamBtnRef = useRef<HTMLButtonElement>(null);

  const addLeadMutation = useAddProjectLead(projectId);
  const removeLeadMutation = useRemoveProjectLead(projectId);
  const addTeamMutation = useAddTeamToProject(projectId);
  const removeTeamMutation = useRemoveTeamFromProject(projectId);

  const tasksQuery = useTasks(projectId);
  const allTeamsQuery = useTeams();

  const taskNameById = useMemo(
    () => new Map((tasksQuery.data ?? []).map((t) => [t.taskId, t.name])),
    [tasksQuery.data],
  );
  const totalTaskCount = tasksQuery.data?.length ?? 0;
  const teamNameById = useMemo(
    () => new Map((allTeamsQuery.data ?? []).map((t) => [t.teamId, t.name])),
    [allTeamsQuery.data],
  );

  // Teams not yet on the palette (candidates for "Add team…").
  const availableTeams = useMemo(() => {
    const onPalette = new Set(paletteTeams.map((t) => t.teamId));
    return (allTeamsQuery.data ?? []).filter((t) => !onPalette.has(t.teamId));
  }, [allTeamsQuery.data, paletteTeams]);

  const filteredMembers = filterQuery.trim()
    ? members.filter((m) => m.displayName.toLowerCase().includes(filterQuery.toLowerCase()))
    : members;

  const leadCount = members.filter((m) => m.sourceKind === 'lead').length;
  const teamCount = members.filter((m) => m.sourceKind === 'team').length;
  const directCount = members.filter((m) => m.sourceKind === 'direct').length;

  async function handleAddLead(userId: string, displayName: string) {
    setAddingLeadOpen(false);
    try {
      await addLeadMutation.mutateAsync({ userId });
      onToast(`${displayName} added as a lead`, 'success');
    } catch (err) {
      onToast(err instanceof Error ? err.message : 'Failed', 'error');
    }
  }

  async function handleConfirmRemoveLead() {
    if (!leadToRemove) return;
    setRemovingLead(true);
    try {
      await removeLeadMutation.mutateAsync({ userId: leadToRemove.userId });
      onToast(`${leadToRemove.displayName} removed as a lead`, 'success');
      setLeadToRemove(null);
      setTimeout(() => addLeadBtnRef.current?.focus(), 0);
    } catch (err) {
      onToast(err instanceof Error ? err.message : 'Failed', 'error');
    } finally {
      setRemovingLead(false);
    }
  }

  async function handleAddTeam(teamId: string, name: string) {
    setAddingTeamOpen(false);
    try {
      await addTeamMutation.mutateAsync({ teamId });
      onToast(`${name} added to the project`, 'success');
    } catch (err) {
      onToast(err instanceof Error ? err.message : 'Failed', 'error');
    }
  }

  async function handleConfirmRemoveTeam() {
    if (!teamToRemove) return;
    setRemovingTeam(true);
    try {
      await removeTeamMutation.mutateAsync({ teamId: teamToRemove.teamId });
      onToast(`${teamToRemove.name} removed from the project`, 'success');
      setTeamToRemove(null);
      setTimeout(() => addTeamBtnRef.current?.focus(), 0);
    } catch (err) {
      onToast(err instanceof Error ? err.message : 'Failed', 'error');
    } finally {
      setRemovingTeam(false);
    }
  }

  return (
    <div>
      {/* Project setup card */}
      <div className="assign-card" aria-label="Project setup">
        <div
          className="assign-card__header"
          style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}
        >
          <h3 className="assign-card__title">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <circle cx="6" cy="5" r="3" />
              <path d="M0 14c0-3.3 2.7-6 6-6h2c3.3 0 6 2.7 6 6" />
            </svg>
            Project setup
          </h3>
          <span style={{ fontSize: 'var(--fs-13)', color: 'var(--fg3)' }}>
            who leads this project, and which teams can be assigned to its tasks
          </span>
        </div>

        {/* Project leads */}
        <div
          className="assign-card__row assign-card__row--wrap"
          role="group"
          aria-label="Project leads"
        >
          <span className="assign-card__label">PROJECT LEADS</span>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'var(--space-2)',
              alignItems: 'center',
            }}
          >
            {leads.map((lead) => (
              <span
                key={lead.userId}
                className="source-chip lead"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, paddingRight: 4 }}
              >
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 12 12"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M6 1l1.5 3 3.5.5-2.5 2.5.6 3.5L6 9l-3.1 1.5.6-3.5L1 4.5 4.5 4z" />
                </svg>
                {lead.displayName}
                {lead.isApprover && (
                  <span
                    style={{
                      fontSize: 'var(--fs-11)',
                      fontWeight: 'var(--fw-semibold, 600)',
                      letterSpacing: '0.03em',
                      textTransform: 'uppercase',
                      color: 'var(--ut-bluebonnet)',
                      background: 'var(--ut-bluebonnet-wash)',
                      padding: '1px 5px',
                      borderRadius: 3,
                    }}
                  >
                    Approver
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setLeadToRemove(lead)}
                  aria-label={`Remove lead ${lead.displayName}`}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'inherit',
                    padding: '0 2px',
                    fontSize: 15,
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </span>
            ))}
            {addingLeadOpen ? (
              <UserSearch
                id="add-lead"
                label="Search for a user to add as a project lead"
                onSelect={(uid, name) => void handleAddLead(uid, name)}
                onCancel={() => {
                  setAddingLeadOpen(false);
                  setTimeout(() => addLeadBtnRef.current?.focus(), 0);
                }}
              />
            ) : (
              <button
                ref={addLeadBtnRef}
                type="button"
                style={{
                  ...ADD_PILL_STYLE,
                  ...(isArchived ? { opacity: 0.6, cursor: 'default' } : {}),
                }}
                onClick={() => setAddingLeadOpen(true)}
                disabled={isArchived}
                title={isArchived ? ARCHIVED_HINT : undefined}
              >
                + Add lead…
              </button>
            )}
          </div>
        </div>

        {/* Teams on this project (palette) */}
        <div
          className="assign-card__row assign-card__row--wrap"
          role="group"
          aria-label="Teams on this project"
        >
          <span className="assign-card__label">TEAMS ON THIS PROJECT</span>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'var(--space-2)',
              alignItems: 'center',
            }}
          >
            {paletteTeams.map((team) => (
              <span
                key={team.teamId}
                className="source-chip team"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, paddingRight: 4 }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: team.color,
                    flexShrink: 0,
                  }}
                />
                {team.name}
                <span style={{ color: 'var(--fg3)', fontSize: 'var(--fs-12)' }}>
                  ·{team.memberCount}
                </span>
                <button
                  type="button"
                  onClick={() => setTeamToRemove(team)}
                  aria-label={`Remove team ${team.name} from project`}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'inherit',
                    padding: '0 2px',
                    fontSize: 15,
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </span>
            ))}
            {addingTeamOpen ? (
              <TeamPicker
                teams={availableTeams}
                onSelect={(id, name) => void handleAddTeam(id, name)}
                onCancel={() => {
                  setAddingTeamOpen(false);
                  setTimeout(() => addTeamBtnRef.current?.focus(), 0);
                }}
              />
            ) : (
              <button
                ref={addTeamBtnRef}
                type="button"
                style={{
                  ...ADD_PILL_STYLE,
                  ...(allTeamsQuery.isLoading || allTeamsQuery.error || isArchived
                    ? { opacity: 0.6, cursor: 'default' }
                    : {}),
                }}
                onClick={() => setAddingTeamOpen(true)}
                disabled={allTeamsQuery.isLoading || !!allTeamsQuery.error || isArchived}
                title={
                  isArchived
                    ? ARCHIVED_HINT
                    : allTeamsQuery.error
                      ? 'Could not load teams — try refreshing'
                      : allTeamsQuery.isLoading
                        ? 'Loading teams…'
                        : undefined
                }
              >
                {allTeamsQuery.isLoading
                  ? 'Loading teams…'
                  : allTeamsQuery.error
                    ? 'Teams unavailable'
                    : '+ Add team…'}
              </button>
            )}
          </div>
        </div>

        {isArchived ? (
          /* A `title` tooltip never renders on a DISABLED control, so the reason
             the two pills above are dead has to be on the page as prose. */
          <p className="pane-archived-note" style={{ margin: 'var(--space-3) 0 0' }}>
            <svg width="14" height="14" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
              <path d="M1 2.5A.5.5 0 0 1 1.5 2h9a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5v-1zM2 5h8v4.5a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5V5zm2.5 1.5a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1h-3z" />
            </svg>
            <span>
              This project is archived, so no new leads or teams can be added. The ones already on
              it can still be removed. Choose <strong>Restore…</strong> above to put it back.
            </span>
          </p>
        ) : (
          <p
            style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--fs-12)', color: 'var(--fg3)' }}
          >
            <span aria-hidden="true">ⓘ </span>
            Assign these teams — or specific people — to individual tasks from the Tasks tab.
          </p>
        )}
      </div>

      {/* Filter + count row */}
      <div className="members-toolbar" style={{ marginTop: 'var(--space-4)' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 340 }}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--fg3)',
              pointerEvents: 'none',
            }}
          >
            <circle cx="6" cy="6" r="4" />
            <path d="M9.5 9.5l3 3" />
          </svg>
          <input
            type="search"
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            placeholder="Filter members…"
            aria-label="Filter project members"
            style={{
              width: '100%',
              padding: '6px 12px 6px 32px',
              border: '1px solid var(--border)',
              borderRadius: 4,
              fontFamily: 'inherit',
              fontSize: 'var(--fs-14)',
              minHeight: 36,
            }}
          />
        </div>
        {members.length > 0 && (
          <span style={{ fontSize: 'var(--fs-12)', color: 'var(--fg3)', whiteSpace: 'nowrap' }}>
            {members.length} member{members.length !== 1 ? 's' : ''}
            {` · ${leadCount} lead${leadCount !== 1 ? 's' : ''}`}
            {` · ${teamCount} from team`}
            {` · ${directCount} direct`}
          </span>
        )}
      </div>

      {filteredMembers.length === 0 ? (
        <p style={{ color: 'var(--fg3)', fontStyle: 'italic' }}>
          {members.length === 0
            ? 'No members yet. Add a team or assign people to tasks from the Tasks tab.'
            : 'No members match your filter.'}
        </p>
      ) : (
        <div className="inbox-table-wrap">
          <table className="members-table" aria-label="Project members">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">How they got here</th>
                <th scope="col">On which tasks</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((m) => (
                <tr key={m.userId}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <MemberAvatar userId={m.userId} initials={m.initials} />
                      <span style={{ fontWeight: 'var(--fw-medium)' }}>{m.displayName}</span>
                    </div>
                  </td>
                  <td>
                    <SourceKindChip
                      kind={m.sourceKind}
                      teamName={m.teamId ? teamNameById.get(m.teamId) : undefined}
                    />
                  </td>
                  <td>
                    <TaskPills
                      onTaskIds={m.onTaskIds}
                      taskNameById={taskNameById}
                      totalTaskCount={totalTaskCount}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={leadToRemove !== null}
        title="Remove lead?"
        message={
          leadToRemove ? (
            <>
              Remove <strong>{leadToRemove.displayName}</strong> as a lead of this project? They
              will lose the ability to manage it (unless they have access in another way). A project
              must keep at least one lead.
            </>
          ) : null
        }
        confirmLabel="Remove lead"
        pendingLabel="Removing…"
        isPending={removingLead}
        onConfirm={() => void handleConfirmRemoveLead()}
        onClose={() => setLeadToRemove(null)}
      />

      <ConfirmDialog
        open={teamToRemove !== null}
        title={teamToRemove ? `Remove ${teamToRemove.name} team?` : 'Remove team?'}
        message={
          teamToRemove ? (
            <>
              <strong>{teamToRemove.name}</strong> will be removed from this project and from every
              task it&apos;s assigned to. People who are on the project only through this team will
              drop off. Already-logged time is preserved.
            </>
          ) : null
        }
        confirmLabel="Remove team"
        pendingLabel="Removing…"
        isPending={removingTeam}
        onConfirm={() => void handleConfirmRemoveTeam()}
        onClose={() => setTeamToRemove(null)}
      />
    </div>
  );
}
